<template>
    <PhotoSelectionBlock
        v-if="imageUrl"
        :image-url="imageUrl"
        :disabled="disabled"
        :label="$t('pages.product_detail_page.ai_upload.transfer.photo_label')"
        :change-label="$t('pages.product_detail_page.ai_upload.transfer.change')"
        @dismiss="onDismiss"
    />
</template>

<script setup lang="ts">
import { openFilePickerTrigger, storedPhotoDataUrl, useAiPhotoTransfer } from '@/composables/ai-upload/useAiPhotoTransfer';
import { useTransferPreview } from '@/composables/useTransferPreview';
import PhotoSelectionBlock from '@/pages/ai-product-page/partials/ai-product-info/partials/PhotoSelectionBlock.vue';
import { computed } from 'vue';

type AiPhotoSelectionBlockProps = {
    disabled?: boolean;
    // The photo is carried and not generated from yet — see showPhotoSelectionBlock.
    isPrimedTransfer?: boolean;
};

const props = withDefaults(defineProps<AiPhotoSelectionBlockProps>(), {
    disabled: false,
    isPrimedTransfer: false,
});

const { transferPreview } = useTransferPreview();
const photoTransfer = useAiPhotoTransfer();

/**
 * The customer's own photo, in every state — the row says "Jouw foto" and offers changing it, so a
 * result made from that photo has no business here. The stored mirror is the authority; the preview
 * only fills in before the mount that seeds it.
 */
const imageUrl = computed<string | undefined>(() => storedPhotoDataUrl.value ?? transferPreview.value);

/**
 * Primed, this row is the carried transfer: dropping it is what turns the panel back into a plain
 * upload. Once it has been generated from, the photo is what the shown result was made from, so it
 * stays until a new crop replaces it — cancelling the picker must not leave the customer with none.
 */
function onDismiss(): void {
    if (props.isPrimedTransfer) {
        photoTransfer.clear();
        transferPreview.value = undefined;
    }

    openFilePickerTrigger.value++;
}
</script>
